import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { BehaviorSubject } from 'rxjs';
import { FeatureType, MappedFeature, PaymentPlan } from 'src/app/models/payment';
import { BillingFrequency, BillingStrategy, PlanAction } from '@pncl/common-models';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { PlanDetailsService } from 'src/app/services/plan-details.service';
import { PencilButtonThemeEnum } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';

const ALWAYS_VISIBLE_CATEGORY_KEYS: FeatureType[] = [
  FeatureType.PRICING,
  FeatureType.LIVE_TEACHING,
];

@Component({
  selector: 'app-plan-list',
  templateUrl: './plan-list.component.html',
  styleUrls: ['./plan-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanListComponent implements OnChanges {
  @Input() plans: PaymentPlan[] = [];
  @Input() currentPlan: PaymentPlan | null = null;
  @Input() billingStrategy!: BillingStrategy;
  @Input() billingFrequency!: BillingFrequency;
  @Input() isInstitutionUser: boolean = false;

  @Output() billingStrategyChange = new EventEmitter<BillingStrategy>();
  @Output() billingFrequencyChange = new EventEmitter<BillingFrequency>();

  @ViewChild('planListTable') planListTable!: ElementRef;

  protected featureCategories = this.planDetailsService.getFeatureCategories();
  protected featuresByCategory = this.planDetailsService.getFeaturesByCategory();
  private readonly collapsedCategories = this.featureCategories.filter((category) =>
    ALWAYS_VISIBLE_CATEGORY_KEYS.includes(category.key),
  );
  protected displayedCategories = this.collapsedCategories;

  /** Whether the comparison table (with always-visible categories) is shown. */
  protected isComparePlans$ = new BehaviorSubject<boolean>(false);
  /** Whether expandable categories beyond Pricing / Live teaching are shown. */
  protected isFullComparison$ = new BehaviorSubject<boolean>(false);

  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  protected readonly IconTypes = IconTypes;
  protected readonly BillingStrategy = BillingStrategy;
  protected readonly BillingFrequency = BillingFrequency;
  protected readonly PlanAction = PlanAction;
  protected readonly supportSeatBasedPlans: boolean = false;
  protected readonly supportAnnualPlans: boolean = false;

  /** Precomputed cell lookup: plan key → feature name → mapped cell. */
  private featureCellsByPlan = new Map<string, Map<MappedFeature['name'], MappedFeature>>();

  constructor(
    public planDetailsService: PlanDetailsService,
    private cdr: ChangeDetectorRef,
    private flagsService: FlagsService,
  ) {
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_BILLING)) {
      this.supportSeatBasedPlans = <boolean>(
        this.flagsService.featureFlagsVariables.enable_billing.support_seat_base_plans
      );
      this.supportAnnualPlans = <boolean>(
        this.flagsService.featureFlagsVariables.enable_billing.support_annual_plans
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.plans) {
      this.rebuildFeatureCellIndex();
    }
  }

  handleBillingStrategyChange(strategy: BillingStrategy) {
    this.billingStrategyChange.emit(strategy);
  }

  handleBillingFrequencyChange(event: MatSlideToggleChange | BillingFrequency) {
    if (event instanceof MatSlideToggleChange) {
      this.billingFrequencyChange.emit(
        event.checked ? BillingFrequency.Annually : BillingFrequency.Monthly,
      );
    } else {
      this.billingFrequencyChange.emit(event);
    }
  }

  /**
   * Cheap per-cell lookup against the index built in ngOnChanges.
   * Feature names are unique across categories, so category is unused.
   */
  getFeatureCell(
    plan: PaymentPlan,
    _category: FeatureType,
    featureName: MappedFeature['name'],
  ): MappedFeature | undefined {
    return this.featureCellsByPlan.get(this.featureCellKey(plan))?.get(featureName);
  }

  private featureCellKey(plan: PaymentPlan): string {
    return `${plan.billingStrategy}:${plan.billingFrequency}:${plan.name}`;
  }

  private rebuildFeatureCellIndex(): void {
    const index = new Map<string, Map<MappedFeature['name'], MappedFeature>>();

    for (const plan of this.plans ?? []) {
      const mapping = this.planDetailsService.getFeatureMappingForPlan({
        billingStrategy: plan.billingStrategy,
        billingFrequency: plan.billingFrequency,
        name: plan.name,
      });
      const cells = new Map<MappedFeature['name'], MappedFeature>();
      mapping?.mappedFeaturesByCategory?.forEach((features) => {
        for (const feature of features) {
          cells.set(feature.name, feature);
        }
      });
      index.set(this.featureCellKey(plan), cells);
    }

    this.featureCellsByPlan = index;
  }

  isHighlightedPlan(plan: PaymentPlan): boolean {
    return !!plan.tag;
  }

  /**
   * Always-visible "See full comparison" control: reveal the table if needed
   * and scroll to its start.
   */
  scrollToComparison(): void {
    if (!this.isComparePlans$.value) {
      this.isComparePlans$.next(true);
      this.cdr.detectChanges();
    }

    this.planListTable?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  /** Expand or collapse categories beyond Pricing / Live teaching. */
  toggleFullComparison(): void {
    const showFullComparison = !this.isFullComparison$.value;
    this.isFullComparison$.next(showFullComparison);
    this.displayedCategories = showFullComparison
      ? this.featureCategories
      : this.collapsedCategories;
  }
}
